import React, { useEffect, useMemo } from 'react'
import { Card, Tab, Nav } from 'react-bootstrap'
import { Eye, CheckCircle2, XCircle } from 'lucide-react'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import SnippetList from './SnippetList'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { ProjectAnnotationType } from '../../../types/models/project'

interface Props {
  contentId: string
  activeTab: string
  onSelectTab: (key: string | null) => void
  untouched: AnnotationDataBoundingBox[]
  accepted: AnnotationDataBoundingBox[]
  rejected: AnnotationDataBoundingBox[]
  selectedBBoxClassIndex?: number
  onSnippetSelect: (box: AnnotationDataBoundingBox) => void
  onAccept: (box: AnnotationDataBoundingBox, e?: React.MouseEvent | KeyboardEvent) => void
  onReject: (box: AnnotationDataBoundingBox, e?: React.MouseEvent | KeyboardEvent) => void
  hoveredBoxIndex?: number
  onHover: (index: number | undefined) => void
}

const PredictionRightPanel: React.FC<Props> = ({
  contentId,
  activeTab,
  onSelectTab,
  untouched,
  accepted,
  rejected,
  selectedBBoxClassIndex,
  onSnippetSelect,
  onAccept,
  onReject,
  hoveredBoxIndex,
  onHover,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const annotationType: ProjectAnnotationType | undefined = project?.annotation_type
  const [rotateLongSideVertical, setRotateLongSideVertical] = React.useState(false)

  const allowedTabs = useMemo<string[]>(() => {
    switch (annotationType) {
      case 'bounding_box':
      case 'oriented_bounding_box':
      case 'classification':
        return ['inspect', 'accepted', 'rejected']
      case 'segmentation':
      default:
        return []
    }
  }, [annotationType])

  const effectiveActiveTab = allowedTabs.includes(activeTab) ? activeTab : allowedTabs[0]

  useEffect(() => {
    if (allowedTabs.length === 0) {
      if (activeTab !== '') {
        onSelectTab(null)
      }
      return
    }
    if (effectiveActiveTab && activeTab !== effectiveActiveTab) {
      onSelectTab(effectiveActiveTab)
    }
  }, [activeTab, allowedTabs, effectiveActiveTab, onSelectTab])

  if (allowedTabs.length === 0) {
    return (
      <Card className="bg-dark border-0 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
        <Card.Body
          className="bg-dark flex-grow-1 d-flex flex-column overflow-hidden"
          style={{ minHeight: 0 }}
        />
      </Card>
    )
  }

  return (
    <Card className="bg-dark border-0 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
      <Card.Body
        className="bg-dark flex-grow-1 d-flex flex-column overflow-hidden"
        style={{ minHeight: 0 }}
      >
        <Tab.Container activeKey={effectiveActiveTab} onSelect={onSelectTab}>
          <Nav variant="pills" className="mb-2 w-100" fill>
            {allowedTabs.includes('inspect') && (
              <Nav.Item className="flex-fill">
                <Nav.Link
                  eventKey="inspect"
                  className="w-100 px-2 py-1"
                  disabled={untouched.length === 0}
                  style={
                    untouched.length === 0
                      ? {
                          color: '#6c757d',
                          borderRadius: '0.5rem',
                          opacity: 0.5,
                          cursor: 'not-allowed',
                        }
                      : effectiveActiveTab === 'inspect'
                      ? {
                          background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
                          color: '#fff',
                          borderRadius: '0.5rem',
                        }
                      : { color: '#e2e8f0', borderRadius: '0.5rem' }
                  }
                >
                  <span className="d-inline-flex flex-column align-items-center gap-1 text-center">
                    <Eye size={16} />
                    <span
                      className="d-inline-flex align-items-center gap-1"
                      style={{ fontSize: '0.85rem' }}
                    >
                      <span className="d-none d-lg-inline">Inspect</span>
                      <span className="badge bg-warning text-dark">{untouched.length}</span>
                    </span>
                  </span>
                </Nav.Link>
              </Nav.Item>
            )}
            {allowedTabs.includes('accepted') && (
              <Nav.Item className="flex-fill">
                <Nav.Link
                  eventKey="accepted"
                  className="w-100 px-2 py-1"
                  disabled={accepted.length === 0}
                  style={
                    accepted.length === 0
                      ? {
                          color: '#6c757d',
                          borderRadius: '0.5rem',
                          opacity: 0.5,
                          cursor: 'not-allowed',
                        }
                      : effectiveActiveTab === 'accepted'
                      ? {
                          background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
                          color: '#fff',
                          borderRadius: '0.5rem',
                        }
                      : { color: '#e2e8f0', borderRadius: '0.5rem' }
                  }
                >
                  <span className="d-inline-flex flex-column align-items-center gap-1 text-center">
                    <CheckCircle2 size={16} />
                    <span
                      className="d-inline-flex align-items-center gap-1"
                      style={{ fontSize: '0.85rem' }}
                    >
                      <span className="d-none d-lg-inline">Accepted</span>
                      <span className="badge bg-success">{accepted.length}</span>
                    </span>
                  </span>
                </Nav.Link>
              </Nav.Item>
            )}
            {allowedTabs.includes('rejected') && (
              <Nav.Item className="flex-fill">
                <Nav.Link
                  eventKey="rejected"
                  className="w-100 px-2 py-1"
                  disabled={rejected.length === 0}
                  style={
                    rejected.length === 0
                      ? {
                          color: '#6c757d',
                          borderRadius: '0.5rem',
                          opacity: 0.5,
                          cursor: 'not-allowed',
                        }
                      : effectiveActiveTab === 'rejected'
                      ? {
                          background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
                          color: '#fff',
                          borderRadius: '0.5rem',
                        }
                      : { color: '#e2e8f0', borderRadius: '0.5rem' }
                  }
                >
                  <span className="d-inline-flex flex-column align-items-center gap-1 text-center">
                    <XCircle size={16} />
                    <span
                      className="d-inline-flex align-items-center gap-1"
                      style={{ fontSize: '0.85rem' }}
                    >
                      <span className="d-none d-lg-inline">Rejected</span>
                      <span className="badge bg-danger">{rejected.length}</span>
                    </span>
                  </span>
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
          {annotationType === 'oriented_bounding_box' && (
            <div className="d-flex justify-content-end mb-2">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="rotateSnippetsSwitchPred"
                  checked={rotateLongSideVertical}
                  onChange={(e) => setRotateLongSideVertical(e.target.checked)}
                />
                <label className="form-check-label text-light" htmlFor="rotateSnippetsSwitchPred">
                  Rotate snippets
                </label>
              </div>
            </div>
          )}
          <Tab.Content className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
            {allowedTabs.includes('inspect') && (
              <Tab.Pane eventKey="inspect">
                <SnippetList
                  contentId={contentId}
                  items={untouched}
                  selectedBBoxClassIndex={selectedBBoxClassIndex}
                  showConfidence={true}
                  showAcceptButton={true}
                  showRejectButton={true}
                  onAccept={onAccept}
                  onReject={onReject}
                  hoveredBoxIndex={hoveredBoxIndex}
                  onHover={onHover}
                  onSelect={onSnippetSelect}
                  rotateLongSideVertical={
                    annotationType === 'oriented_bounding_box' && rotateLongSideVertical
                  }
                />
              </Tab.Pane>
            )}
            {allowedTabs.includes('accepted') && (
              <Tab.Pane eventKey="accepted">
                <SnippetList
                  items={accepted}
                  contentId={contentId}
                  selectedBBoxClassIndex={selectedBBoxClassIndex}
                  showConfidence={true}
                  showAcceptButton={false}
                  showRejectButton={true}
                  onReject={onReject}
                  hoveredBoxIndex={hoveredBoxIndex}
                  onHover={onHover}
                  onSelect={(box) => onSnippetSelect(box)}
                  rotateLongSideVertical={
                    annotationType === 'oriented_bounding_box' && rotateLongSideVertical
                  }
                />
              </Tab.Pane>
            )}
            {allowedTabs.includes('rejected') && (
              <Tab.Pane eventKey="rejected">
                <SnippetList
                  items={rejected}
                  contentId={contentId}
                  selectedBBoxClassIndex={selectedBBoxClassIndex}
                  showConfidence={true}
                  showAcceptButton={true}
                  showRejectButton={false}
                  onAccept={onAccept}
                  hoveredBoxIndex={hoveredBoxIndex}
                  onHover={onHover}
                  onSelect={(box) => onSnippetSelect(box)}
                  rotateLongSideVertical={
                    annotationType === 'oriented_bounding_box' && rotateLongSideVertical
                  }
                />
              </Tab.Pane>
            )}
          </Tab.Content>
        </Tab.Container>
      </Card.Body>
    </Card>
  )
}

export default PredictionRightPanel
